import { fetchHttpIntegrationsById } from "../../services/state/http-integration/httpIntegration-async-calls";
import { fetchConnectedSystemsByApplication } from "../../services/state/connected-systems/connectedSystem-async-calls";
import { AppDispatch, RootState } from "@/services/state/store";
import { useMonaco } from "@monaco-editor/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import {
  Box,
  Input,
  useDisclosure,
  useToast,
  Text,
  Center,
  Checkbox,
  IconButton,
  Icon,
  Textarea,
  VStack,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import {
  setSelectedHttpIntegration,
  setSelectedHttpIntegrationIo,
} from "../../services/state/http-integration/httpIntegration";
import { ConnectedSystem } from "@/services/state/connected-systems/connectedSystem-interface";
import {
  HttpIntegrationHeader,
  HttpIntegrationIo,
  HttpIntegrationQueryParams,
} from "@/services/state/http-integration/httpIntegration-interface";
import { getAxiosClient } from "../../services/HttpsService";
import { DropDown, Option } from "../../components/Select/DropDown";
import { CiCircleChevDown, CiCircleChevUp } from "react-icons/ci";
import { FaMinus } from "react-icons/fa6";
import {
  fetchCustomDataTypesByApplication,
  fetchDataTypes,
} from "../../services/state/custom-data-types/customDataTypes-async-calls";
import { isStringLiteral } from "typescript";
import { AxiosResponse } from "axios";

export const useHttpIntegration = () => {
  const dispatch: AppDispatch = useDispatch();
  const toast = useToast();
  const monaco = useMonaco();
  const { intgId } = useParams();

  const [selectedConnectedSystem, setSelectedConnectedSystem] =
    useState<ConnectedSystem>();
  const [selectedMethod, setSelectedMethod] = useState<string>("GET");
  const [selectedContentType, setSelectedContentType] =
    useState<string>("application/json");
  const [requestBody, setRequestBody] = useState<string>("{}");
  const [requestBodyMarker, setRequestBodyMarker] = useState<Object[]>([]);
  const [testResponse, setTestResponse] = useState<AxiosResponse<any, any>>();
  const [sizes, setSizes] = useState([200, 200, 200]);

  const { isOpen: isEditing, onToggle: onEditToggle } = useDisclosure();

  const methods = ["GET", "POST", "PUT", "PATCH", "DELETE"];

  const contentTypes = [
    "application/json",
    "application/x-www-form-urlencoded",
    "multipart/form-data",
  ];

  const selectedHttpIntegration = useSelector(
    (state: RootState) => state.httpIntegrations.selectedHttpIntegration
  );
  const selectedHttpIntegrationIo = useSelector(
    (state: RootState) => state.httpIntegrations.selectedHttpIntegrationIo
  );
  const selectedHttpIntegrationInitial = useSelector(
    (state: RootState) => state.httpIntegrations.selectedHttpIntegrationInitial
  );
  const connectedSystems = useSelector(
    (state: RootState) => state.connectedSystems.connectedSystems
  );
  const dataTypes = useSelector(
    (state: RootState) => state.customDataTypes.dataTypes
  );
  const cdt = useSelector(
    (state: RootState) => state.customDataTypes.customDataTypes
  );

  const [headers, setHeaders] = useState<HttpIntegrationHeader[]>([]);
  const [queryParams, setQueryParams] = useState<HttpIntegrationQueryParams[]>(
    []
  );
  const [testInputs, setTestInputs] = useState(selectedHttpIntegrationIo);

  const placeholders: string[] = [];

  const dataTypeOptions = dataTypes.map((type, index) => ({
    id: type.dataTypCd,
    name: type.dataTypNm,
    component: <Text key={index}>{type.dataTypNm}</Text>,
  }));

  const cdtOptions = cdt.map((it) => ({
    id: it.uuid,
    name: it.dataTypeName,
    component: <Text>{it.dataTypeName}</Text>,
  }));

  useEffect(() => {
    dispatch(fetchHttpIntegrationsById(intgId as string));
    dispatch(fetchDataTypes());
  }, []);

  useEffect(() => {
    if (selectedHttpIntegration) {
      setSelectedConnectedSystem(selectedHttpIntegration.connSystem);
      setSelectedMethod(selectedHttpIntegration.intgMethod || "GET");
      setSelectedContentType(
        selectedHttpIntegration.body?.at(0)?.contTyp || contentTypes[0]
      );
      setRequestBody(selectedHttpIntegration.body?.at(0)?.reqBody || "");
      setHeaders(selectedHttpIntegration.headers || []);
      setQueryParams(selectedHttpIntegration.queryParams || []);
      dispatch(
        fetchConnectedSystemsByApplication(selectedHttpIntegration.app.uuid)
      );
      dispatch(
        fetchCustomDataTypesByApplication(selectedHttpIntegration.app.uuid)
      );
    }
  }, [selectedHttpIntegrationInitial]);

  useEffect(() => {
    setTestInputs(selectedHttpIntegrationIo);
  }, [selectedHttpIntegrationIo]);

  const handleResize = (index: number, newWidth: number) => {
    const updatedSizes = [...sizes];
    updatedSizes[index] = newWidth;
    setSizes(updatedSizes);
  };

  const handleAddHeader = () => {
    setHeaders([
      ...headers,
      {
        id: headers.length + 1,
        intgUuid: selectedHttpIntegration?.uuid || "",
        headNm: "",
        headVal: "",
        headSeq: headers.length,
        isActive: "",
        createdBy: "",
        createdOn: "",
        updatedBy: "",
        updatedOn: "",
      },
    ]);
  };

  const handleRemoveHeader = (id: number) => {
    setHeaders(headers.filter((header) => header.id !== id));
  };

  const handleHeaderChange = (
    id: number,
    field: "headNm" | "headVal",
    value: string
  ) => {
    setHeaders(
      headers.map((header) =>
        header.id === id ? { ...header, [field]: value } : header
      )
    );
  };

  const moveHeader = (index: number, direction: "up" | "down") => {
    const newHeaders = [...headers];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= headers.length) return;
    [newHeaders[index], newHeaders[targetIndex]] = [
      newHeaders[targetIndex],
      newHeaders[index],
    ];
    setHeaders(newHeaders);
  };

  const handleAddQueryParam = () => {
    setQueryParams([
      ...queryParams,
      {
        id: queryParams.length + 1,
        integrationUuid: selectedHttpIntegration?.uuid || "",
        paramNm: "",
        paramVal: "",
        paramSeq: queryParams.length,
        isActive: "",
        createdBy: "",
        createdOn: "",
        updatedBy: "",
        updatedOn: "",
      },
    ]);
  };

  const handleRemoveQueryParam = (id: number) => {
    setQueryParams(queryParams.filter((query) => query.id !== id));
  };

  const handleQueryParamChange = (
    id: number,
    field: "paramNm" | "paramVal",
    value: string
  ) => {
    setQueryParams(
      queryParams.map((query) =>
        query.id === id ? { ...query, [field]: value } : query
      )
    );
  };

  const moveQueryParam = (index: number, direction: "up" | "down") => {
    const newQueryParams = [...queryParams];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= queryParams.length) return;
    [newQueryParams[index], newQueryParams[targetIndex]] = [
      newQueryParams[targetIndex],
      newQueryParams[index],
    ];
    setQueryParams(newQueryParams);
  };

  const handleEditorChange = (value: string | undefined) => {
    setRequestBody(value || "");
  };

  const defineGroovyLanguage = (
    monacoInstance: any,
    getTestInputs: () => HttpIntegrationIo[]
  ) => {
    if (!monacoInstance) return;

    const tokenizer = {
      tokenizer: {
        root: [
          [
            /iv!([a-zA-Z_$][\w$]*|\d+[a-zA-Z_$][\w$]*|[a-zA-Z_$][\w$]*\d+)/,
            "constant",
          ],
          [/str![a-zA-Z_$][\w$]*(?:\s*\([^\)]*\))?/, "string"],
          [/int!\d+/, "number"],
          [/\b\d+(\.\d+)?\b/, "number"],
          [/\/\*[\s\S]*?\*\//, "comment"],
          [/\/\/.*$/, "comment"],
        ],
        string: [
          [/[^\\"]+/, "string"],
          [/\\./, "string.escape"],
          [/"/, "string", "@pop"],
        ],
      },
    };

    monacoInstance.languages.setMonarchTokensProvider("json", tokenizer);
    monacoInstance.languages.setMonarchTokensProvider("python", tokenizer);

    // Define custom theme for Groovy syntax highlighting
    monacoInstance.editor.defineTheme("custom-theme", {
      base: "vs",
      inherit: true,
      rules: [
        { token: "constant", foreground: "008000", fontStyle: "italic" },
        { token: "string", foreground: "FFA500", fontStyle: "italic" },
        { token: "number", foreground: "FFA500", fontStyle: "italic" },
      ],
      colors: {
        "editor.foreground": "#000000",
        "editor.background": "#FFFFFF",
      },
    });

    const completion = {
      triggerCharacters: ["!"],
      provideCompletionItems: (model: any, position: any) => {
        const textBeforeCursor = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: Math.max(1, position.column - 20),
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });

        // Get the latest value of testInputs
        const testInputs = getTestInputs();

        // Suggest rule! unique matches
        if (textBeforeCursor.endsWith("iv!")) {
          const suggestions = Array.from(
            new Set(testInputs.map((input) => input.paramNm))
          )
            .map((input) => "iv!" + input)
            .map((suggestion) => ({
              label: suggestion,
              kind: monacoInstance.languages.CompletionItemKind.Text,
              insertText: suggestion,
              range: {
                startLineNumber: position.lineNumber,
                startColumn: position.column - 3,
                endLineNumber: position.lineNumber,
                endColumn: position.column,
              },
            }));

          return { suggestions };
        }

        return { suggestions: [] };
      },
    };

    // Register completion provider for custom rule suggestion
    const provider = monacoInstance.languages.registerCompletionItemProvider(
      "json",
      completion
    );
    const providerPython =
      monacoInstance.languages.registerCompletionItemProvider(
        "python",
        completion
      );

    // Dispose provider on unmount to avoid duplicate instances
    return () => {
      provider.dispose();
      providerPython.dispose();
    };
  };

  useEffect(() => {
    if (monaco) {
      defineGroovyLanguage(monaco, () => testInputs);
    }
  }, [monaco, placeholders, testInputs]);

  // Editor options, including the custom theme and language
  const editorOptions = {
    selectOnLineNumbers: true,
    minimap: { enabled: false },
    readOnly: !isEditing,
  };

  //   const isEdited =
  //     JSON.stringify(selectedHttpIntegration) !==
  //       JSON.stringify(selectedHttpIntegrationInitial) ||
  //     selectedHttpIntegration?.connSystem.uuid !==
  //       selectedConnectedSystem?.uuid ||
  //     selectedHttpIntegration?.intgMethod !== selectedMethod ||
  //     (selectedHttpIntegration.headers &&
  //       headers.length !== 0 &&
  //       JSON.stringify(selectedHttpIntegration.headers) !==
  //         JSON.stringify(headers)) ||
  //     (selectedHttpIntegration.queryParameters &&
  //       queryParams.length !== 0 &&
  //       JSON.stringify(selectedHttpIntegration.queryParameters) !==
  //         JSON.stringify(queryParams)) ||
  //     (selectedHttpIntegration.body?.length === 0 &&
  //       requestBody.length !== 0 &&
  //       selectedHttpIntegration.body?.at(0)?.reqBody !== requestBody);

  const isEdited = isEditing;

  const onValueChange = (
    feild: "intgNm" | "intgDesc" | "relativePath",
    value: string
  ) => {
    if (selectedHttpIntegration) {
      const selectedHttpIntegrationCopy = { ...selectedHttpIntegration };
      selectedHttpIntegrationCopy[feild] = value;
      dispatch(setSelectedHttpIntegration(selectedHttpIntegrationCopy));
    }
  };

  const columns = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Type",
      accessor: "type",
    },
    {
      Header: "Is Multiple",
      accessor: "isMultiple",
    },
    {
      Header: "Order",
      accessor: "order",
    },
    {
      Header: "",
      accessor: "actions",
    },
  ];

  const selectedIntgInputsData = selectedHttpIntegration
    ? selectedHttpIntegrationIo.map((param, index) => ({
        name: (
          <Input
            key={index}
            value={param.paramNm}
            onChange={(e) => {
              const params = [...selectedHttpIntegrationIo];
              params[index] = {
                ...params[index],
                paramNm: e.target.value,
              };
              dispatch(setSelectedHttpIntegrationIo(params));
            }}
            width="10vw"
            readOnly={!isEditing}
          />
        ),
        type: (
          <Box key={index} width="10vw">
            <VStack>
              <DropDown
                selected={dataTypeOptions.find(
                  (type) => type.id === param.dataTypeCd
                )}
                title={<Text></Text>}
                options={dataTypeOptions}
                onOptionChange={function (
                  selectedOptions: Option | Option[]
                ): void {
                  if (!Array.isArray(selectedOptions)) {
                    const params = [...selectedHttpIntegrationIo];
                    params[index] = {
                      ...params[index],
                      dataTypeCd: selectedOptions.id,
                    };
                    dispatch(setSelectedHttpIntegrationIo(params));
                  }
                }}
                disabled={!isEditing}
              />
              {param.dataTypeCd === "ANY" && (
                <DropDown
                  selected={cdtOptions.find((cdt) => cdt.id === param.other)}
                  title={<Text></Text>}
                  options={cdtOptions}
                  onOptionChange={function (
                    selectedOptions: Option | Option[]
                  ): void {
                    if (!Array.isArray(selectedOptions)) {
                      const params = [...selectedHttpIntegrationIo];
                      params[index] = {
                        ...params[index],
                        other: selectedOptions.id,
                      };
                      dispatch(setSelectedHttpIntegrationIo(params));
                    }
                  }}
                  disabled={!isEditing}
                />
              )}
            </VStack>
          </Box>
        ),
        isMultiple: (
          <Center>
            <Checkbox
              defaultChecked
              variant="solid"
              colorScheme="brand"
              onChange={(e) => {
                const params = [...selectedHttpIntegrationIo];
                params[index] = {
                  ...params[index],
                  isMultiple: e.target.checked,
                };
                dispatch(setSelectedHttpIntegrationIo(params));
              }}
              isChecked={param.isMultiple}
              isReadOnly={!isEditing}
            />
          </Center>
        ),
        order: (
          <Center key={index}>
            <IconButton
              as={CiCircleChevUp as React.ElementType}
              boxSize="24px"
              aria-label="Move Up"
              onClick={() => {
                if (index > 0) {
                  const params = [...selectedHttpIntegrationIo];
                  [params[index], params[index - 1]] = [
                    params[index - 1],
                    params[index],
                  ]; // Swap with previous
                  dispatch(setSelectedHttpIntegrationIo(params));
                }
              }}
              variant="unstyled"
              isDisabled={!isEditing || index === 0}
            />
            <IconButton
              as={CiCircleChevDown as React.ElementType}
              boxSize="24px"
              aria-label="Move Down"
              onClick={() => {
                if (index < selectedHttpIntegrationIo.length - 1) {
                  const params = [...selectedHttpIntegrationIo];
                  [params[index], params[index + 1]] = [
                    params[index + 1],
                    params[index],
                  ]; // Swap with next
                  dispatch(setSelectedHttpIntegrationIo(params));
                }
              }}
              variant="unstyled"
              isDisabled={
                !isEditing || index === selectedHttpIntegrationIo.length - 1
              } // Disable for the last item
            />
          </Center>
        ),
        actions: isEditing ? (
          <Center key={index}>
            <Icon
              as={FaMinus as React.ElementType}
              color="red.500"
              cursor="pointer"
              onClick={() => {
                const params = [...selectedHttpIntegrationIo];
                params.splice(index, 1);
                dispatch(setSelectedHttpIntegrationIo(params));
              }}
            />
          </Center>
        ) : null,
      }))
    : [];

  const columnData = [...selectedIntgInputsData];

  const handleTestInputChange = (index: number, value: string) => {
    const newTestInputs = [...selectedHttpIntegrationIo];
    newTestInputs[index] = {
      ...newTestInputs[index],
      testValue: value,
    };
    dispatch(setSelectedHttpIntegrationIo(newTestInputs));
  };

  const testInputColumns = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Type",
      accessor: "type",
    },
    {
      Header: "Value",
      accessor: "value",
    },
  ];

  useEffect(() => {
    selectedHttpIntegrationIo.forEach((param, index) => {
      if (param.dataTypeCd === "BOOL") {
        const isValidBoolean =
          param.testValue === "true" || param.testValue === "false";
        if (!isValidBoolean) {
          handleTestInputChange(index, "true");
        }
      }

      if (param.dataTypeCd === "DATE") {
        const isValidDate = !isNaN(Date.parse(param.testValue || ""));
        if (!isValidDate) {
          const today = new Date().toISOString().split("T")[0];
          handleTestInputChange(index, today);
        }
      }

      // Handle date_time default values
      if (param.dataTypeCd === "DATE_TIME") {
        const isValidDateTime = !isNaN(Date.parse(param.testValue || ""));
        if (!isValidDateTime) {
          const now = new Date().toISOString();
          handleTestInputChange(index, now);
        }
      }
    });
  }, [selectedHttpIntegrationIo, handleTestInputChange]);

  const selectedRuleTestInputsData = testInputs.map((param, index) => ({
    name: <Text key={index}>{param.paramNm}</Text>,
    type: <Text key={index}>{param.dataTypeCd}</Text>,
    value:
      param.dataTypeCd === "ANY" ? (
        <Textarea
          value={param.testValue || ""}
          onChange={(e) => handleTestInputChange(index, e.target.value)}
        />
      ) : param.dataTypeCd === "BOOL" && !param.isMultiple ? (
        <RadioGroup
          defaultValue="true"
          onChange={(e) => handleTestInputChange(index, e)}
        >
          <Stack spacing={5} direction="row">
            <Radio colorScheme="brand" value="true">
              True
            </Radio>
            <Radio colorScheme="brand" value="false">
              False
            </Radio>
          </Stack>
        </RadioGroup>
      ) : param.dataTypeCd === "DATE" && !param.isMultiple ? (
        <Input
          value={param.testValue || ""}
          onChange={(e) => handleTestInputChange(index, e.target.value)}
          placeholder="Select Date and Time"
          size="md"
          type="date"
          colorScheme="brand"
        />
      ) : param.dataTypeCd === "DATE_TIME" && !param.isMultiple ? (
        <Input
          value={param.testValue || ""}
          onChange={(e) => handleTestInputChange(index, e.target.value)}
          placeholder="Select Date and Time"
          size="md"
          type="datetime-local"
          colorScheme="brand"
        />
      ) : (
        <Input
          value={param.testValue || ""}
          onChange={(e) => handleTestInputChange(index, e.target.value)}
        />
      ),
  }));

  const testInputColumnData = [...selectedRuleTestInputsData];

  const onAddField = () => {
    const params: HttpIntegrationIo[] = [
      {
        id: 0,
        integrationUuid: "",
        paramNm: "",
        dataTypeCd: "STR",
        isMultiple: false,
        paramIo: 0,
        isActive: "",
        createdBy: "",
        createdOn: "",
        updatedBy: "",
        updatedOn: "",
      },
      ...selectedHttpIntegrationIo,
    ];
    dispatch(setSelectedHttpIntegrationIo(params));
  };

  const onTestInput = async () => {
    let hasError = false;
    const result: { [key: string]: any } = {};

    // Iterate over the testInputs and process values based on dataTypeCd
    testInputs.forEach((param) => {
      const { paramNm, testValue, dataTypeCd, isMultiple } = param;
      var array = [];

      // If parameter name is missing, show error
      if (!paramNm) {
        toast({
          title: "Error",
          description: "Param name cannot be empty.",
          status: "error",
          position: "top",
          duration: 3000,
          isClosable: true,
        });
        hasError = true;
        return;
      }

      try {
        if (isMultiple && Array.isArray(JSON.parse(testValue as string))) {
          array = JSON.parse(testValue as string);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: `Input for parameter "${paramNm}" must be a valid Array.`,
          status: "error",
          position: "top",
          duration: 3000,
          isClosable: true,
        });
        hasError = true;
      }

      if (dataTypeCd === "ANY") {
        // For 'ANY' type, validate and parse JSON
        try {
          result[paramNm] = JSON.parse(testValue || "");
        } catch (e) {
          toast({
            title: "Error",
            description: `Input for parameter "${paramNm}" must be a valid JSON.`,
            status: "error",
            position: "top",
            duration: 3000,
            isClosable: true,
          });
          hasError = true;
        }
      } else if (dataTypeCd === "INT") {
        // For 'INT' type, validate and convert to integer
        if (isMultiple) {
          const castedArray = array.map((it: any) => parseInt(it));
          if (
            castedArray !== 0 &&
            castedArray.filter((it: any) => isNaN(it)).length !== 0
          ) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid integer array.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = castedArray;
          }
        } else {
          const intValue = parseInt(testValue || "", 10);
          if (isNaN(intValue)) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid integer.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = intValue;
          }
        }
      } else if (dataTypeCd === "STR") {
        if (isMultiple) {
          const castedArray = array.map((it: any) => it.toString());
          if (
            castedArray !== 0 &&
            castedArray.filter((it: any) => isStringLiteral(it)).length !== 0
          ) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid string array.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = castedArray;
          }
        } else {
          // For 'STR' type, validate and convert to integer
          if (!testValue) {
            toast({
              title: "Error",
              description: `Input for parameter "${paramNm}" must be a valid string.`,
              status: "error",
              position: "top",
              duration: 3000,
              isClosable: true,
            });
            hasError = true;
          } else {
            result[paramNm] = testValue;
          }
        }
      } else {
        // For other types, treat the value as a string
        result[paramNm] = testValue;
      }
    });

    if (!hasError) {
      const url = ((selectedHttpIntegration?.connSystem.baseUrl as string) +
        selectedHttpIntegration?.relativePath) as string;

      const queryParamsJson: { [key: string]: any } = {};
      const headersJson: { [key: string]: any } = {};

      queryParams.forEach((qp) => {
        queryParamsJson[qp.paramNm] = qp.paramVal;
      });
      headers.forEach((header) => {
        headersJson[header.headNm] = header.headVal;
      });

      const data = {
        url,
        method: selectedMethod,
        contentType: selectedContentType,
        requestBodyString: requestBody === "" ? "{}" : requestBody,
        headers: headersJson,
        authHeaders: {},
        queryParams: queryParamsJson,
        inputs: result,
      };

      const response = await getAxiosClient().post(
        "/main/test-http-integration",
        data
      );

      setTestResponse(response.data);

      return response;
    }
  };

  const onSave = async () => {
    let hasError = false;

    if (selectedHttpIntegration && selectedHttpIntegration["intgNm"] === "") {
      toast({
        title: "Error saving integration system",
        description: "Integration name cannot be empty",
        status: "error",
        position: "top",
        duration: 3000,
        isClosable: true,
      });
      hasError = true;
    }

    if (requestBodyMarker?.length !== 0) {
      console.log(requestBodyMarker);
      toast({
        title: "Error saving integration system",
        description: "Errors found in JSON in request body",
        status: "error",
        position: "top",
        duration: 3000,
        isClosable: true,
      });
      hasError = true;
    }

    if (headers.length !== 0) {
      const headersNames = headers.map((header) => header.headNm);
      const headersNamesSet = new Set(headersNames);

      if (headersNames.some((nm) => nm.trim() === "")) {
        toast({
          title: "Error saving integration system",
          description: "Header name cannot be empty",
          status: "error",
          position: "top",
          duration: 3000,
          isClosable: true,
        });
        hasError = true;
      }

      if (headersNames.length !== headersNamesSet.size) {
        toast({
          title: "Error saving integration system",
          description: "Header name cannot repeat",
          status: "error",
          position: "top",
          duration: 3000,
          isClosable: true,
        });
        hasError = true;
      }
    }

    if (queryParams.length !== 0) {
      const paramsNames = queryParams.map((param) => param.paramNm);
      const paramsNamesSet = new Set(paramsNames);

      if (paramsNames.some((nm) => nm.trim() === "")) {
        toast({
          title: "Error saving integration system",
          description: "Query param name cannot be empty",
          status: "error",
          position: "top",
          duration: 3000,
          isClosable: true,
        });
        hasError = true;
      }

      if (paramsNames.length !== paramsNamesSet.size) {
        toast({
          title: "Error saving integration system",
          description: "Query param name cannot repeat",
          status: "error",
          position: "top",
          duration: 3000,
          isClosable: true,
        });
        hasError = true;
      }
    }

    if (hasError) return;

    const data = {
      id: selectedHttpIntegration?.id,
      uuid: selectedHttpIntegration?.uuid,
      intgNm: selectedHttpIntegration?.intgNm,
      appUuid: selectedHttpIntegration?.app.uuid,
      csUuid: selectedConnectedSystem?.uuid,
      intgDesc: selectedHttpIntegration?.intgDesc,
      intgMethod: selectedMethod,
      relativePath: selectedHttpIntegration?.relativePath,
      contentType: selectedContentType,
      body: requestBody,
      headers: headers.map((header) => ({
        headNm: header.headNm,
        headVal: header.headVal,
        headSeq: header.headSeq,
      })),
      queryParams: queryParams.map((qr) => ({
        paramNm: qr.paramNm,
        paramVal: qr.paramVal,
        paramSeq: qr.paramSeq,
      })),
      intgIo: selectedHttpIntegrationIo.map((io, index) => ({
        paramNm: io.paramNm,
        dataTypCd: io.dataTypeCd,
        paramIo: "I",
        paramSeq: index,
        other: io.other,
        isMultiple: io.isMultiple,
      })),
    };

    const response = await getAxiosClient().post(
      "/api/http-integration/update-integration",
      data
    );

    dispatch(fetchHttpIntegrationsById(intgId as string));

    if (isEditing) onEditToggle();
  };

  return {
    sizes,
    selectedHttpIntegration,
    selectedConnectedSystem,
    selectedMethod,
    selectedContentType,
    requestBody,
    requestBodyMarker,
    connectedSystems,
    methods,
    contentTypes,
    headers,
    queryParams,
    editorOptions,
    isEdited,
    isEditing,
    columns,
    columnData,
    testInputColumns,
    testInputColumnData,
    testInputs,
    testResponse,
    handleResize,
    onEditToggle,
    setSelectedConnectedSystem,
    setSelectedMethod,
    setSelectedContentType,
    setRequestBodyMarker,
    handleAddHeader,
    handleRemoveHeader,
    handleHeaderChange,
    moveHeader,
    moveQueryParam,
    handleQueryParamChange,
    handleRemoveQueryParam,
    handleAddQueryParam,
    handleEditorChange,
    onAddField,
    onTestInput,
    onValueChange,
    onSave,
  };
};
